import { useState, useEffect, Fragment } from 'react';
import { Button, LoadingSpinner, Input, Select } from '../../components/ui';
import { api } from '../../services/api-client';

interface ActivityRow {
  logId: string;
  timestamp: string;
  userSub: string | null;
  userName: string | null;
  userEmail: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  success: boolean;
}

const ACTION_LABELS: Record<string, string> = {
  USER_LOGIN: 'Login',
  USER_LOGOUT: 'Logout',
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  FIRST_ACCESS_OTP_SENT: 'OTP primo accesso',
  REGISTRATION_COMPLETED: 'Registrazione completata',
  USER_CENSED: 'Utente censito',
  EMAIL_CHANGED: 'Email modificata',
  PASSWORD_RESET_BY_ADMIN: 'Reset password (admin)',
  MFA_RESET_BY_ADMIN: 'Reset MFA (admin)',
  PERMISSIONS_MODIFIED: 'Permessi modificati',
};

const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'Tutte le azioni' },
  { value: 'LOGIN', label: 'Login' },
  { value: 'LOGOUT', label: 'Logout' },
  { value: 'USER_CENSED', label: 'Utente censito' },
  { value: 'REGISTRATION_COMPLETED', label: 'Registrazione completata' },
  { value: 'EMAIL_CHANGED', label: 'Email modificata' },
  { value: 'PASSWORD_RESET_BY_ADMIN', label: 'Reset password (admin)' },
  { value: 'MFA_RESET_BY_ADMIN', label: 'Reset MFA (admin)' },
  { value: 'PERMISSIONS_MODIFIED', label: 'Permessi modificati' },
];

/** Cognito inserts this placeholder when a standard attribute is not set. */
function isPlaceholder(value: string): boolean {
  return value.includes('cognito:default_val');
}

/**
 * Resolve the best available display name for an activity row.
 * Priority: joined user_profile → name/email inside audit details → raw sub.
 * Cognito placeholder values (cognito:default_val) are treated as empty.
 */
function resolveUser(entry: ActivityRow): string {
  if (entry.userName) return entry.userName;
  if (entry.userEmail) return entry.userEmail;
  const d = entry.details || {};
  if (typeof d.name === 'string' && d.name.trim() && !isPlaceholder(d.name)) return d.name;
  if (typeof d.email === 'string' && d.email.trim() && !isPlaceholder(d.email)) return d.email;
  return entry.userSub || '-';
}

function formatContext(details: Record<string, unknown> | null): string {
  if (!details) return '';
  const parts: string[] = [];
  const dev = details.device as Record<string, string> | undefined;
  const geo = details.geolocation as Record<string, string> | undefined;
  if (dev?.browser) parts.push(dev.browser);
  if (dev?.os) parts.push(dev.os);
  if (geo?.city) parts.push(`📍 ${geo.city}${geo.country ? ', ' + geo.country : ''}`);
  return parts.join(' • ');
}

/**
 * Admin — Global user activity log (audit_log, all users).
 */
export function LogAttivitaPage() {
  const [entries, setEntries] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (logId: string) => {
    setExpandedId((prev) => (prev === logId ? null : logId));
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '30');
      if (search) params.set('search', search);
      if (actionFilter) params.set('action', actionFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const response = await api.get<{
        data: ActivityRow[];
        pagination: { totalPages: number; totalItems: number };
      }>(`/admin/logs/activity?${params.toString()}`);
      setEntries(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
    } catch {
      setEntries([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, [page, actionFilter, dateFrom, dateTo]);

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else loadLogs();
  };

  const resetFilters = () => {
    setSearch('');
    setActionFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <div className="intranet-page page-wide">
      <div className="page-header">
        <h2>Log — Attività utente</h2>
      </div>

      <div className="filters-panel">
        <div className="filters-grid">
          <Input
            label="Cerca"
            placeholder="Nome, email, azione..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <Select
            label="Azione"
            options={ACTION_FILTER_OPTIONS}
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          />
          <Input
            label="Dal"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          />
          <Input
            label="Al"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          />
        </div>
        <div className="filters-actions">
          <Button variant="secondary" size="sm" onClick={applyFilters}>
            Cerca
          </Button>
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            Azzera
          </Button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <p>Nessuna attività registrata.</p>
        </div>
      ) : (
        <>
          <div className="audit-table-wrapper">
            <table className="audit-table">
              <thead>
                <tr>
                  <th style={{ width: '32px' }}></th>
                  <th>Data</th>
                  <th>Utente</th>
                  <th>Azione</th>
                  <th>Contesto</th>
                  <th>IP</th>
                  <th>Esito</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const isExpanded = expandedId === entry.logId;
                  return (
                    <Fragment key={entry.logId}>
                      <tr
                        className={`audit-row audit-row--clickable${isExpanded ? ' audit-row--expanded' : ''}`}
                        onClick={() => toggleExpand(entry.logId)}
                      >
                        <td className="audit-expand-toggle">{isExpanded ? '▼' : '▶'}</td>
                        <td className="audit-date">
                          {new Date(entry.timestamp).toLocaleString('it-IT', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </td>
                        <td>{resolveUser(entry)}</td>
                        <td>
                          <span className={`audit-action audit-action--${entry.action.toLowerCase()}`}>
                            {ACTION_LABELS[entry.action] || entry.action}
                          </span>
                        </td>
                        <td className="audit-details">{formatContext(entry.details) || '-'}</td>
                        <td>{entry.ipAddress || '-'}</td>
                        <td>{entry.success ? '✅' : '❌'}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="audit-detail-row">
                          <td colSpan={7}>
                            <AuditDetailView entry={entry} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" role="navigation" aria-label="Paginazione log attività">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                ← Precedente
              </Button>
              <span className="pagination-info">Pagina {page} di {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                Successiva →
              </Button>
            </div>
          )}

          {totalItems > 0 && (
            <p className="pagination-summary">{totalItems.toLocaleString('it-IT')} eventi</p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Expanded detail view for a single activity row.
 * Shows device / geolocation / auth context extracted from audit details.
 */
function AuditDetailView({ entry }: { entry: ActivityRow }) {
  const d = (entry.details || {}) as Record<string, unknown>;
  const device = d.device as Record<string, string> | undefined;
  const geo = d.geolocation as Record<string, string> | undefined;

  return (
    <div className="audit-detail-grid">
      <DetailField label="Azione" value={ACTION_LABELS[entry.action] || entry.action} />
      <DetailField label="Data/Ora" value={new Date(entry.timestamp).toLocaleString('it-IT')} />
      <DetailField label="Esito" value={entry.success ? 'Successo' : 'Fallito'} />
      <DetailField label="Risorsa" value={entry.resourceType || '-'} />
      <DetailField label="Utente" value={resolveUser(entry)} />
      <DetailField label="IP Address" value={entry.ipAddress || '-'} />

      {device && (
        <>
          <DetailField label="Browser" value={device.browser || '-'} />
          <DetailField label="Sistema Operativo" value={device.os || '-'} />
          <DetailField label="Mobile" value={device.mobile === 'true' ? 'Sì' : 'No'} />
          {device.raw && (
            <div style={{ gridColumn: '1 / -1' }}>
              <DetailField label="User Agent" value={device.raw} />
            </div>
          )}
        </>
      )}

      {geo && (
        <>
          <DetailField label="Città" value={geo.city || '-'} />
          <DetailField label="Regione" value={geo.region || '-'} />
          <DetailField label="Paese" value={geo.country || '-'} />
          <DetailField label="Provider" value={geo.org || '-'} />
          <DetailField label="Coordinate" value={geo.loc || '-'} />
        </>
      )}

      {!!d.email && !isPlaceholder(String(d.email)) && <DetailField label="Email" value={String(d.email)} />}
      {!!d.name && !isPlaceholder(String(d.name)) && <DetailField label="Nome" value={String(d.name)} />}
      {!!d.timezone && <DetailField label="Fuso orario" value={String(d.timezone)} />}
      {!!d.language && <DetailField label="Lingua" value={String(d.language)} />}
      {!!d.screenResolution && <DetailField label="Risoluzione schermo" value={String(d.screenResolution)} />}
      {!!d.clientId && <DetailField label="Client ID" value={String(d.clientId)} />}
      {!!d.triggerSource && <DetailField label="Tipo autenticazione" value={String(d.triggerSource)} />}
      {!!d.groups && <DetailField label="Gruppi" value={String(d.groups)} />}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="audit-detail-field">
      <div className="audit-detail-label">{label}</div>
      <div className="audit-detail-value">{value}</div>
    </div>
  );
}
